import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  getApiV1LocationCitiesQueryOptions,
  usePostApiV1LocationCities,
  usePutApiV1LocationCitiesId,
} from '@/shared/api/generated';
import type { CityFormValues } from '../types/cityForm';

/** Callbacks do salvamento de Cidade, com caminho específico para conflito (409). */
export interface SaveCityCallbacks {
  readonly onSuccess?: () => void;
  /** Chamado quando a API retorna 409 (código já cadastrado). */
  readonly onConflict?: () => void;
  /** Chamado para qualquer outro erro. */
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar uma Cidade.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/location/cities` e
 * `PUT /api/v1/location/cities/{id}`), escolhendo a operação pela presença de
 * `id`, invalidando o cache da listagem no sucesso e mapeando o status **409**
 * (código já cadastrado) para o callback `onConflict`.
 */
export function useSaveCity() {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1LocationCities();
  const updateMutation = usePutApiV1LocationCitiesId();

  const invalidate = () => queryClient.invalidateQueries(getApiV1LocationCitiesQueryOptions());

  const handleError = (error: unknown, callbacks?: SaveCityCallbacks) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      callbacks?.onConflict?.();
    } else {
      callbacks?.onError?.();
    }
  };

  /** Salva uma cidade. Cria quando `id` é `undefined`; caso contrário, atualiza. */
  const save = (values: CityFormValues, id: number | undefined, callbacks?: SaveCityCallbacks) => {
    const data = {
      code: values.code,
      name: values.name,
      stateId: values.stateId,
      ...(values.population != null ? { population: values.population } : {}),
    };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };

    if (id === undefined) {
      createMutation.mutate(
        { data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    } else {
      updateMutation.mutate(
        { id, data },
        { onSuccess, onError: error => handleError(error, callbacks) },
      );
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
